import { Skeleton } from "components/new-stuff/ui/Skeleton"
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline"
import { Collapse, Form, Input, Pagination, Select } from "antd"
import filterIcon from "assets/svg/filter.svg"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { Button as CustomButton } from "components"
import { useEffect, useMemo, useState } from "react"
import { useGet } from "utils/useFetch"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { debounce } from "lodash"
import { IEnrolledEmployeeRes } from "../typings"
import {
  formatRate,
  formatTimeAdherence,
  getInitials,
  groupByDepartment,
} from "./utils/timesheet_utils"
import CustomEmptyState from "./component/CustomEmptyState"

// ─── Types ────────────────────────────────────────────────────────────────────

export interface IOnFinishBody {
  department?: DepartmentEntityOrShiftPlanEntity[] | null
  shiftPlan?: DepartmentEntityOrShiftPlanEntity[] | null
}

export interface DepartmentEntityOrShiftPlanEntity {
  label: string
  value: number
  key: number
}

// ─── Component ────────────────────────────────────────────────────────────────

const EnrolledEmployeeWorkplace = ({
  hasActiveShiftPlan,
  onViewShiftPlan,
}: {
  hasActiveShiftPlan: boolean
  onViewShiftPlan: () => void
}) => {
  const { x } = useParams()
  const [searchParams] = useSearchParams()
  const [form] = Form.useForm()

  const [search, setSearch] = useState("")
  const [inputValue, setInputValue] = useState("")
  const [queryParams, setQueryParams] = useState("")
  const [activeDeptKey, setActiveDeptKey] = useState<string | string[]>("")
  const [filterModal, setFilterModal] = useState(false)
  const [page, setPage] = useState({ currentPage: 1, pageSize: 10 })
  const navigate = useNavigate()

  // ─── Data fetching ──────────────────────────────────────────────────────────

  const dutyLocationIdRaw = searchParams.get("dutyLocationId")
  const dutyLocationId = dutyLocationIdRaw ? Number(dutyLocationIdRaw) : null
  const employeesQueryParams = (() => {
    const params = new URLSearchParams(queryParams)
    if (search) params.set("keyword", search)
    else params.delete("keyword")
    if (dutyLocationId) params.set("dutyLocationId", String(dutyLocationId))
    else params.delete("dutyLocationId")
    return params.toString()
  })()

  const { data, isLoading } = useGet<IEnrolledEmployeeRes>({
    url: `/v1/time-attendance/workplaces/${x}/employees?option=enrolled${
      employeesQueryParams ? `&${employeesQueryParams}` : ""
    }`,
    instanceKey: "ewaAxiosInstance",
    enabled: !!x,
  })

  // ─── Derived data ───────────────────────────────────────────────────────────

  const docs = data?.data?.docs || []

  const groupedEmployees = useMemo(() => groupByDepartment(docs), [docs])

  const departmentGroups = useMemo(
    () => Object.values(groupedEmployees),
    [groupedEmployees]
  )

  const shiftPlanOptions = docs
    .map((e) => e.currentShiftPlan)
    .filter(Boolean)
    .filter((plan, i, self) => self.findIndex((p) => p?.id === plan?.id) === i)
    .map((plan) => ({ label: plan?.title, value: plan?.id }))

  const departmentOptions = docs
    .map((e) => e.department)
    .filter(Boolean)
    .filter((dept, i, self) => self.findIndex((p) => p?.id === dept?.id) === i)
    .map((dept) => ({ label: dept?.name, value: dept?.id }))

  // ─── Debounced search ───────────────────────────────────────────────────────

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value)
        setPage((prev) => ({ ...prev, currentPage: 1 }))
      }, 500),
    []
  )

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleOnFinish = (values: IOnFinishBody) => {
    const params = new URLSearchParams()
    if (values.department?.length) {
      params.append(
        "departmentId",
        values.department.map((d) => d.value).join(",")
      )
    }
    if (values.shiftPlan?.length) {
      params.append(
        "shiftPlanId",
        values.shiftPlan.map((s) => s.value).join(",")
      )
    }
    setQueryParams(params.toString())
    setFilterModal(false)
  }

  const handleReset = () => {
    form.resetFields()
    setQueryParams("")
    setFilterModal(false)
  }
  const employees = departmentGroups

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {employees.length <= 0 ? (
        hasActiveShiftPlan ? (
          <CustomEmptyState
            imageUrl="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/a83ed003-5f63-472a-8475-d5c45fd6e446.svg"
            onClick={onViewShiftPlan}
            title="View shift plan"
            top="75"
            showIcon={false}
          />
        ) : (
          <CustomEmptyState
            imageUrl="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/4bbfb557-98b3-4377-93e6-63a05fba2ecc.png"
            onClick={() =>
              navigate(
                `/dashboard/time-and-attendance/create-shift-plan-workplace/${x}`
              )
            }
            title="Create shift plan"
            top="75"
          />
        )
      ) : (
        <div>
          {/* ── Search & filter bar ── */}
          <div className="flex items-center gap-5">
            <Input
              placeholder="Enter for an employee"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value)
                debouncedSetSearch(e.target.value)
              }}
              style={{ width: "100%", height: "40px" }}
              prefix={
                <MagnifyingGlassIcon className="w-4 h-4 text-neutral-300" />
              }
              className=""
            />
            <span
              className="border-2 border-[#E2E8F0] py-2 px-4 h-10 w-24 rounded-md flex items-center gap-2 cursor-pointer"
              onClick={() => setFilterModal(true)}
            >
              <img
                src={filterIcon}
                width={20}
                height={20}
              />
              <p>Filter</p>
            </span>
          </div>

          {/* ── Employee table ── */}
          {isLoading ? (
            // The header row is static, so it renders straight away and the
            // placeholder stands in only for the department rows below it.
            <div
              className="border border-gray-300 rounded-t-lg overflow-hidden mt-5"
              aria-busy="true"
            >
              <ul className="grid grid-cols-5 items-center bg-slate-50 border-b border-slate-200 text-slate-500 h-11 py-3 px-4 font-semibold">
                <li>Name</li>
                <li>Shift plan enrolled in</li>
                <li>Shift completed this week</li>
                <li>All-time absenteeism rate</li>
                <li>Total time-adherence rate</li>
              </ul>
              {Array.from({ length: 4 }, (_, row) => (
                <div
                  key={row}
                  className="grid grid-cols-5 items-center gap-4 px-4 h-[52px] border-b border-slate-100"
                >
                  {Array.from({ length: 5 }, (_, col) => (
                    <Skeleton
                      key={col}
                      // Uneven widths, or the block reads as a rendering fault
                      // rather than as content on its way.
                      width={col === 0 ? "70%" : "50%"}
                    />
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-gray-300 rounded-t-lg overflow-hidden mt-5">
              <ul className="grid grid-cols-5 items-center bg-slate-50 border-b border-slate-200 text-slate-500 h-11 py-3 px-4 font-semibold">
                <li>Name</li>
                <li>Shift plan enrolled in</li>
                <li>Shift completed this week</li>
                <li>All-time absenteeism rate</li>
                <li>Total time-adherence rate</li>
              </ul>
              <Collapse
                style={{ background: "transparent" }}
                className="[&_.ant-collapse-item]:py-2"
                bordered={false}
                accordion
                expandIcon={() => null}
                activeKey={activeDeptKey}
                onChange={(keys) => setActiveDeptKey(keys.toString())}
                items={employees.map((dept) => ({
                  key: String(dept.departmentId),
                  label: (
                    <div className="flex justify-between items-center w-full">
                      <p>{dept.departmentName}</p>
                      <button className="flex items-center gap-2">
                        {activeDeptKey === String(dept.departmentId) ? (
                          <>
                            <span>Collapse</span>
                            <ChevronUpIcon
                              width={16}
                              height={16}
                            />
                          </>
                        ) : (
                          <>
                            <span>Expand</span>
                            <ChevronDownIcon
                              width={16}
                              height={16}
                            />
                          </>
                        )}
                      </button>
                    </div>
                  ),
                  children: (
                    <>
                      {dept.employees.map((employee) => (
                        <ActiveEnrolledEmployeeRow
                          key={employee.companyId}
                          firstName={employee.firstName}
                          lastName={employee.lastName}
                          shiftPlanName={
                            employee.currentShiftPlan?.title || "—"
                          }
                          completedShift={`${employee.totalClockIns} of ${employee.totalShiftsAssignedAllTime}`}
                          absenteeismRate={formatRate(
                            employee.totalShiftsMissed,
                            employee.totalShiftsAssignedAllTime
                          )}
                          timeAdherence={formatTimeAdherence(
                            employee.totalShiftsAssignedAllTime,
                            employee.totalEarlyClockOuts
                          )}
                        />
                      ))}
                    </>
                  ),
                }))}
              />
            </div>
          )}

          {/* ── Pagination ── */}
          <div className="flex justify-start mt-6 mb-3">
            <Pagination
              current={page.currentPage}
              pageSize={page.pageSize}
              total={data?.data?.totalDocs || 0}
              showSizeChanger
              pageSizeOptions={[5, 10, 20, 50]}
              onChange={(page, pageSize) =>
                setPage({ currentPage: page, pageSize })
              }
            />
          </div>

          {/* ── Filter modal ── */}
          <CustomModal
            rootClassName="ta-scope"
            width={600}
            open={filterModal}
            title="Filters for enrolled employees"
            onCancel={() => setFilterModal(false)}
          >
            <Form
              form={form}
              onFinish={handleOnFinish}
            >
              <p className="font-semibold pb-2">Filter by department</p>
              <Form.Item name="department">
                <Select
                  mode="multiple"
                  placeholder="Search for a department"
                  labelInValue
                  style={{ width: "100%", height: "40px" }}
                  optionLabelProp="label"
                  suffixIcon={
                    <ChevronDownIcon
                      width={16}
                      height={16}
                    />
                  }
                  options={departmentOptions}
                  className=""
                />
              </Form.Item>

              <p className="font-semibold pb-2">Filter by shift plan</p>
              <Form.Item name="shiftPlan">
                <Select
                  mode="multiple"
                  placeholder="Search for a shift plan"
                  labelInValue
                  style={{ width: "100%", height: "40px" }}
                  optionLabelProp="label"
                  suffixIcon={
                    <ChevronDownIcon
                      width={16}
                      height={16}
                    />
                  }
                  options={shiftPlanOptions}
                  className=""
                />
              </Form.Item>

              <Form.Item>
                <div className="flex items-center gap-3 mt-3">
                  <CustomButton
                    style={{ width: "100%", height: "40px" }}
                    onClick={handleReset}
                    className="whitespace-nowrap border-2 border-gray-300 rounded-md"
                  >
                    Reset filters
                  </CustomButton>
                  <CustomButton
                    color="primary"
                    style={{ width: "100%", height: "40px" }}
                    onClick={() => form.submit()}
                    className="whitespace-nowrap"
                  >
                    Apply filters
                  </CustomButton>
                </div>
              </Form.Item>
            </Form>
          </CustomModal>
        </div>
      )}
    </>
  )
}

export default EnrolledEmployeeWorkplace

// ─── Sub-components ───────────────────────────────────────────────────────────

type ActiveEnrolledRowProps = {
  firstName: string
  lastName: string
  shiftPlanName: string
  completedShift: string
  absenteeismRate: string
  timeAdherence: string
}

const ActiveEnrolledEmployeeRow = ({
  firstName,
  lastName,
  shiftPlanName,
  completedShift,
  absenteeismRate,
  timeAdherence,
}: ActiveEnrolledRowProps) => {
  const fullName = `${firstName} ${lastName}`
  return (
    <div className="grid grid-cols-5 gap-3 items-center border-y border-y-gray-200 py-3">
      <div className="flex items-center gap-2 shrink-0">
        <span className="rounded-full bg-[#E2E8F0] w-10 h-10 flex items-center justify-center font-semibold text-sm">
          {getInitials(fullName)}
        </span>
        <p className="truncate min-w-0 hover:underline">{fullName}</p>
      </div>
      <p>{shiftPlanName}</p>
      <p>{completedShift}</p>
      <p>{absenteeismRate}</p>
      <p>{timeAdherence}</p>
    </div>
  )
}
